import React, { useCallback, useEffect, useRef, useState } from 'react';
import { trackingClient } from 'clients';
import { CAR_TRACKING_PAGE_SIZE } from 'clients/TrackingClient';
import { HTTPError } from 'clients/HTTPClient';
import { CarTracking, CarTrackingStatus } from 'types/CarTracking';
import { SpotInterface } from 'types';
import LinesIcon from 'assets/icons/lines.svg?react';
import {
    dateTimeWithTimezone,
    dateWithTimeZone,
    getYearMonthDayFromString,
    minutesToStringRepresentation,
} from 'utils/helpers';
import { RentalType } from 'utils/constants';
import AGTAbleWrapper from 'components/AGTable/AGTableWrapper';
import InfoTooltip from 'components/Dashboard/SpotCard/InfoTooltip/InfoTooltip';
import SingleDateSelector from 'components/SingleDateSelector/SingleDateSelector';
import AGSimpleSelect from 'components/AGSelect/AGSimpleSelect/AGSimpleSelect';
import SearchInput from 'components/AGForm/Inputs/SearchInput/SearchInput';
import Loader from 'components/Loader/Loader';
import Pagination from 'components/Pagination/Pagination';
import AGMessage from 'components/AGMessage/AGMessage';
import { XDaysAgo } from 'components/AGDatePicker/utils';
import AGMultipleSelect from 'components/AGSelect/AGMultipleSelect/AGMultipleSelect';
import Modal, { ModalRef } from 'components/Modal/Modal';
import styles from './DriverLog.module.css';
import { MissingImage } from 'components/MissingImage/MissingImage';

type DriverLogProps = {
    spot: SpotInterface;
    timezone: string;
    filters: {
        date: string;
        status: CarTrackingStatus | '';
        currentSearchTerm: string;
        page: string;
        searchTerm: string;
        rentalType: RentalType | '';
    };
    urlUpdater: Record<'date' | 'status' | 'page' | 'rentalType', (value: string | null) => void> & {
        searchTerm: (search: string) => void;
    };
    isSuperuserView: boolean;
    isEnabled: boolean;
    isDemo?: boolean;
};

const STATUS_OPTIONS = [
    { value: '', label: 'All types' },
    { value: CarTrackingStatus.ENFORCED, label: 'Enforced' },
    { value: CarTrackingStatus.GRACE_PERIOD, label: 'Grace period' },
    { value: CarTrackingStatus.RENTER, label: 'Renter' },
    { value: CarTrackingStatus.VIOLATOR, label: 'Violator' },
    { value: CarTrackingStatus.VISITOR, label: 'Visitor' },
];

// The Type column renders the raw session status, which for slot-based sessions is the
// underscored rental_type value (e.g. "bulk_rental"). Map those to the human-readable names
// used elsewhere in the Owner Dashboard. Other values rely on the column's `capitalize` CSS.
const STATUS_DISPLAY_LABELS: Record<string, string> = {
    bulk_rental: 'Tenant',
    free_until_hourly: 'Free until hourly',
};

function formatTrackingStatus(status: CarTracking['status']): string {
    if (!status) return '';
    return STATUS_DISPLAY_LABELS[status] ?? status;
}

function getRentalTypeOptions(spot: SpotInterface) {
    const options = [
        { value: RentalType.Hourly, label: 'Hourly' },
        { value: RentalType.Flat, label: 'Flat Rate' },
    ];
    if (spot.price_daily) options.push({ value: RentalType.Daily, label: 'Daily' });
    if (spot.price_monthly) options.push({ value: RentalType.Monthly, label: 'Monthly' });

    return options;
}

function getImagesUrls(trackingEvent: CarTracking): { plateImg: string | null; fullImg: string | null } {
    const { entrance_license_plate_image: entrancePlateImg, exit_license_plate_image: exitPlateImg } = trackingEvent;

    let plateImg = null;
    let fullImg = null;

    if (entrancePlateImg) {
        plateImg = entrancePlateImg;
        fullImg = trackingEvent.entrance_image || null;
    } else if (exitPlateImg) {
        plateImg = exitPlateImg;
        fullImg = trackingEvent.exit_image || null;
    }

    return { plateImg, fullImg };
}

function DriverLog({
    spot,
    filters,
    urlUpdater,
    timezone,
    isSuperuserView: superuserView,
    isEnabled,
    isDemo = false,
}: DriverLogProps): JSX.Element {
    const [carTrackingEvents, setCarTrackingEvents] = useState<CarTracking[]>([]);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [totalPages, setTotalPages] = useState(1);
    const [completeImage, setCompleteImage] = useState<string | null>(null);

    const today = new Date();
    const rentalTypeOptions = getRentalTypeOptions(spot);
    const imageModalRef = useRef<ModalRef>();

    const getCarTrackingEvents = useCallback(
        async function _getCarTrackingEvents(
            date: string,
            page: number,
            search: string,
            status: CarTrackingStatus | '',
            rentalType: RentalType | '',
            isDemo = false
        ) {
            setLoading(true);
            try {
                const { month, day, year } = getYearMonthDayFromString(date);
                const response = await trackingClient.getCarTrackingEvents(spot.uuid, {
                    page,
                    start: dateWithTimeZone(timezone, year, month - 1, day, 0, 0).getTime() / 1000,
                    end: dateWithTimeZone(timezone, year, month - 1, day, 23, 59).getTime() / 1000,
                    plate: search,
                    status: status === CarTrackingStatus.RENTER ? rentalType : status,
                    isDemo,
                });
                setTotalPages(Math.ceil(response.count / CAR_TRACKING_PAGE_SIZE));
                setCarTrackingEvents(response.results);
            } catch (e) {
                setTotalPages(1);
                if (HTTPError.isHTTPError(e)) {
                    setErrorMessage(e.message);
                } else {
                    setErrorMessage(e?.toString() || 'Something went wrong');
                }
            } finally {
                setLoading(false);
            }
        },
        [spot, timezone]
    );

    useEffect(() => {
        getCarTrackingEvents(
            filters.date,
            Number(filters.page),
            filters.searchTerm,
            filters.status,
            filters.rentalType,
            isDemo
        );
    }, [
        getCarTrackingEvents,
        filters.date,
        filters.page,
        filters.searchTerm,
        filters.status,
        filters.rentalType,
        isDemo,
    ]);

    function searchRentals(event: React.ChangeEvent<HTMLInputElement>) {
        urlUpdater.searchTerm(event.target.value);
        urlUpdater.page('1');
    }

    function selectDate(date: Date | undefined) {
        urlUpdater.date(date?.toLocaleDateString() || '');
        urlUpdater.page('1');
    }

    function selectStatus(status: string) {
        urlUpdater.status(status);
        urlUpdater.page('1');
        if (status === CarTrackingStatus.RENTER)
            urlUpdater.rentalType(rentalTypeOptions.map((rt) => rt.value).join(','));
        else urlUpdater.rentalType('');
    }

    function handleSlotRentalTypeChange(rentalType: RentalType): void {
        urlUpdater.rentalType(rentalType);
        urlUpdater.page('1');
    }

    function seeCompleteImage(img: string | null) {
        if (img && superuserView) {
            setCompleteImage(img);
            imageModalRef.current?.openModal();
        }
    }

    return (
        <div
            className={`visibility-section ${styles.driverLogContainer} ${
                !superuserView && !isEnabled ? 'superuserView-off' : ''
            }`}
        >
            <div className={styles.header}>
                <h3>Driver Log</h3>
                <div className={styles.filters}>
                    <SingleDateSelector
                        date={filters.date ? new Date(filters.date) : undefined}
                        onSave={selectDate}
                        placeHolder="Select Start Date"
                        allowDeSelect={false}
                        latestAvailableDate={today}
                        earliestAvailableDate={XDaysAgo(today, 6)}
                        noIcon
                        dropdownWidth="280px"
                        disabled={loading}
                    />
                    <AGSimpleSelect
                        selected={filters.status}
                        onSelect={selectStatus}
                        options={STATUS_OPTIONS}
                        icon={<LinesIcon />}
                        width="200px"
                        disabled={loading}
                    />

                    {filters.status === CarTrackingStatus.RENTER && (
                        <AGMultipleSelect
                            selectedValues={filters.rentalType.split(',')}
                            onSelect={(selection) => handleSlotRentalTypeChange(selection.join(',') as RentalType)}
                            options={rentalTypeOptions}
                            icon={<LinesIcon />}
                            placeholder="Rental type"
                        />
                    )}
                    <SearchInput
                        onChange={searchRentals}
                        value={filters.currentSearchTerm}
                        placeholder="Search by plate"
                        type="text"
                        className={styles.search}
                        width="200px"
                        name="plate"
                        disabled={loading}
                    />
                </div>
            </div>
            {errorMessage && <AGMessage color="error" title={errorMessage} />}
            <AGTAbleWrapper>
                <thead>
                    <tr className={styles.whiteBackground}>
                        <th>License Plate</th>
                        <th>Plate Image</th>
                        <th>Type</th>
                        <th>Date/Time In</th>
                        <th>Date/Time Out</th>
                        <th>Duration</th>
                        <th>All-time Rentals</th>
                        {superuserView && (
                            <th>
                                All-time Visits
                                <InfoTooltip
                                    text={`This is the total number of visits as of today, ${today.toLocaleDateString()}`}
                                />
                            </th>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan={7}>
                                <Loader />
                            </td>
                        </tr>
                    ) : carTrackingEvents.length === 0 ? (
                        <tr>
                            <td colSpan={7}>Driver log information will show up here.</td>
                        </tr>
                    ) : (
                        carTrackingEvents.map((trackingEvent) => {
                            const { entrance_timestamp, exit_timestamp } = trackingEvent;
                            const { plateImg, fullImg } = getImagesUrls(trackingEvent);
                            const durationInMinutes =
                                entrance_timestamp && exit_timestamp
                                    ? Math.round((exit_timestamp - entrance_timestamp) / 60)
                                    : undefined;

                            return (
                                <tr key={trackingEvent.pk}>
                                    <td>
                                        {trackingEvent.plate}
                                        {trackingEvent.state ? ` - ${trackingEvent.state}` : ''}
                                    </td>
                                    <td className={styles.centerTableCell}>
                                        {plateImg ? (
                                            <button
                                                onClick={() => seeCompleteImage(fullImg)}
                                                className={`${styles.imgButton} ${
                                                    fullImg && superuserView ? styles.hasFullImg : ''
                                                }`}
                                            >
                                                <img
                                                    src={plateImg}
                                                    alt=""
                                                    className={styles.plateImage}
                                                    onError={(e) => {
                                                        e.currentTarget.className = styles.brokenImage;
                                                    }}
                                                />
                                            </button>
                                        ) : (
                                            <div style={{ width: 'max-content', margin: '0 auto' }}>
                                                <MissingImage
                                                    style={{ width: 52, height: 40 }}
                                                    tooltip="No image was captured, but the driver started a parking session themselves."
                                                    data-testid="missing-license-plate"
                                                />
                                            </div>
                                        )}
                                    </td>
                                    <td className={styles.status}>{formatTrackingStatus(trackingEvent.status)}</td>
                                    <td>
                                        {entrance_timestamp
                                            ? dateTimeWithTimezone(new Date(entrance_timestamp * 1000), timezone)
                                            : ''}
                                    </td>
                                    <td>
                                        {exit_timestamp
                                            ? dateTimeWithTimezone(new Date(exit_timestamp * 1000), timezone)
                                            : ''}
                                    </td>
                                    <td>
                                        {typeof durationInMinutes !== 'number'
                                            ? ''
                                            : durationInMinutes > 30
                                            ? minutesToStringRepresentation(durationInMinutes)
                                            : 'Under 30min'}
                                    </td>
                                    <td>{trackingEvent.rental_count}</td>
                                    {superuserView && <td>{trackingEvent.visit_count}</td>}
                                </tr>
                            );
                        })
                    )}
                </tbody>
                {totalPages > 1 && !loading && (
                    <tfoot>
                        <tr>
                            <td colSpan={superuserView ? 8 : 7}>
                                <div style={{ display: 'flex', justifyContent: 'center' }}>
                                    <Pagination
                                        currentPage={Number(filters.page)}
                                        onPageChange={urlUpdater.page}
                                        totalPages={totalPages}
                                    />
                                </div>
                            </td>
                        </tr>
                    </tfoot>
                )}
            </AGTAbleWrapper>

            {superuserView && (
                <Modal ref={imageModalRef} onClose={() => setCompleteImage(null)} type="fullSizeImage">
                    {completeImage && <img src={completeImage} alt="" className={styles.fullImg} />}
                </Modal>
            )}
        </div>
    );
}

export default DriverLog;
